import { useEffect, useState } from 'react';

import { Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';

import { Edit, Trash2 } from 'lucide-react';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { brandChipSx } from '../../components/admin/brandChip';
import { AddFeatureFlagModal } from '../../components/feature-flags/AddFeatureFlagModal';
import { DeleteFeatureFlagModal } from '../../components/feature-flags/DeleteFeatureFlagModal';
import { EditFeatureFlagModal } from '../../components/feature-flags/EditFeatureFlagModal';
import { LAYER_NORM_NONE } from '../../constants/layers.ts';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { featureFlagsReducer } from '../../store/reducers/featureFlags.reducer';
import { userReducer } from '../../store/reducers/user.reducer';
import {
  getFeatureFlags,
  getFeatureFlagsError,
  getIsLoadingFeatureFlags,
} from '../../store/selectors/featureFlags.selector';
import { getAllUsers, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { FeatureFlag } from '../../types/featureFlags';

const { loadFeatureFlags } = featureFlagsReducer.actions;
const { loadUsers } = userReducer.actions;

export const FeatureFlagsPage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const featureFlags = useAppSelector(getFeatureFlags);
  const isLoading = useAppSelector(getIsLoadingFeatureFlags);
  const error = useAppSelector(getFeatureFlagsError);
  const allUsers = useAppSelector(getAllUsers);

  // Modal state management
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFeatureFlag, setSelectedFeatureFlag] = useState<FeatureFlag | null>(null);

  // Modal handlers
  const handleEditFeatureFlag = (flag: FeatureFlag) => {
    setSelectedFeatureFlag(flag);
    setEditModalOpen(true);
  };

  const handleAddFeatureFlag = () => {
    setAddModalOpen(true);
  };

  const handleDeleteFeatureFlag = (flag: FeatureFlag) => {
    setSelectedFeatureFlag(flag);
    setDeleteModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedFeatureFlag(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedFeatureFlag(null);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  // Check if user is admin, redirect to forbidden if not
  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  // Load feature flags and users when component mounts (only for admin users)
  useEffect(() => {
    if (user && user.roles.includes('ADMIN')) {
      dispatch(loadFeatureFlags());
      dispatch(loadUsers());
    }
  }, [dispatch, user]);

  // Don't render anything if user is not admin (will redirect)
  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  return (
    <Box>
      <AdminPageHeader
        title="Feature Flags"
        subtitle="Manage application feature flags and toggles."
        action={
          <Button
            variant="contained"
            sx={{
              bgcolor: '#B3EC49',
              color: 'black',
              '&:hover': {
                bgcolor: 'rgba(179, 236, 73, 0.8)',
              },
              borderRadius: '24px',
              px: 3,
            }}
            onClick={handleAddFeatureFlag}
          >
            Add Feature Flag
          </Button>
        }
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!isLoading && !error && featureFlags.length === 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No feature flags found
          </Typography>
        </Box>
      )}

      {!isLoading && !error && featureFlags.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {featureFlags.map((flag) => {
            const targetsLabel =
              flag.targets.length > 0
                ? flag.targets
                    .map((target) => {
                      const targetUser = allUsers.find((u) => u.id === target.userId);
                      return targetUser ? `${targetUser.fullName} (${targetUser.email})` : `User ${target.userId}`;
                    })
                    .join(', ')
                : LAYER_NORM_NONE;
            return (
              <Paper
                key={flag.id}
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: { xs: 1.5, md: 2 },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {flag.name}{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: 'monospace' }}
                    >
                      ({flag.key})
                    </Typography>
                  </Typography>
                  {flag.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}
                    >
                      {flag.description}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.75,
                    flexShrink: 0,
                  }}
                >
                  <Chip
                    size="small"
                    label={flag.enabled ? 'Enabled' : 'Disabled'}
                    sx={brandChipSx(flag.enabled ? 'primary' : 'outlined')}
                  />
                  <Chip size="small" label={`${flag.rolloutPercentage}%`} sx={brandChipSx('outlined')} />
                  <Tooltip title={targetsLabel} placement="top">
                    <Chip
                      size="small"
                      label={`${flag.targets.length} ${flag.targets.length === 1 ? 'target' : 'targets'}`}
                      sx={brandChipSx('outlined')}
                    />
                  </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
                  <IconButton size="small" onClick={() => handleEditFeatureFlag(flag)} color="primary">
                    <Edit size={16} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteFeatureFlag(flag)} color="error">
                    <Trash2 size={16} />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {selectedFeatureFlag && (
        <EditFeatureFlagModal open={editModalOpen} onClose={handleCloseEditModal} featureFlag={selectedFeatureFlag} />
      )}

      {selectedFeatureFlag && deleteModalOpen && (
        <DeleteFeatureFlagModal featureFlag={selectedFeatureFlag} onClose={handleCloseDeleteModal} />
      )}

      <AddFeatureFlagModal open={addModalOpen} onClose={handleCloseAddModal} />
    </Box>
  );
};
